import { HandRaisedIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';

export const HighFiveButton: React.FC<{
  currentValue: number;
  onUpdate: (newValue: number) => void;
  size?: 'default' | 'tiny';
}> = ({ currentValue, onUpdate, size = 'default' }) => {
  const [hasHighFived, setHasHighFived] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation if inside a link
    if (hasHighFived) return;

    setHasHighFived(true);
    onUpdate(currentValue + 1);
  };

  if (size === 'tiny') {
    return (
      <motion.button
        type="button"
        onClick={handleClick}
        disabled={hasHighFived}
        whileHover={!hasHighFived ? { scale: 1.1 } : {}}
        whileTap={!hasHighFived ? { scale: 0.9 } : {}}
        className={`relative flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
          hasHighFived
            ? 'bg-yellow-100 text-yellow-600'
            : 'text-yellow-400 hover:bg-yellow-50 hover:text-yellow-500'
        }`}
      >
        <HandRaisedIcon className="h-4 w-4" />
        <AnimatePresence>
          {hasHighFived && (
            <motion.span
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: 1, y: -15, scale: 1.2 }}
              exit={{ opacity: 0 }}
              className="absolute -top-1 left-1/2 -translate-x-1/2 text-sm pointer-events-none"
            >
              ✨
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      disabled={hasHighFived}
      whileHover={!hasHighFived ? { scale: 1.05 } : {}}
      whileTap={!hasHighFived ? { scale: 0.95 } : {}}
      className={`group flex items-center gap-2 rounded-full px-4 py-2 transition-all ${
        hasHighFived
          ? 'cursor-default bg-yellow-100 text-yellow-800'
          : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 hover:shadow-md'
      }`}
    >
      <span className="font-bold text-sm">
        {hasHighFived ? 'High Fived!' : 'High Five!'}
      </span>

      <div className="relative">
        <HandRaisedIcon
          className={`h-5 w-5 transition-transform ${
            hasHighFived
              ? 'text-yellow-600'
              : 'text-yellow-500 group-hover:-rotate-12'
          }`}
        />
        <AnimatePresence>
          {hasHighFived && (
            <motion.span
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: 1, y: -20, scale: 1.2 }}
              exit={{ opacity: 0 }}
              className="absolute -right-1 -top-1 text-lg"
            >
              ✨
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div
        className={`ml-1 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold ${
          hasHighFived
            ? 'bg-yellow-200 text-yellow-800'
            : 'bg-white text-yellow-600 ring-1 ring-yellow-200'
        }`}
      >
        {currentValue}
      </div>
    </motion.button>
  );
};
